// src/badges.js — définition des badges (clé, catégorie, glyphe, palier, points)
// La progression/earned est calculée côté backend (GET /badges/:userId).
// `points` = valeur du badge (plus rare/dur = plus de points) → barre de progression profil.
// Icônes = placeholders (médaillon coloré + glyphe SVG) → à remplacer par les visuels du designer.

export const CATEGORY_COLORS = {
    dives: "#0077b6",     // jalons de plongées
    explore: "#2e9e5b",   // exploration
    social: "#e0a400",    // social
    contrib: "#0096c7",   // contribution (conditions)
    variety: "#6f4a9e",   // variété / saisons / régularité
};

// Ordre = ordre d'affichage : paliers d'une même famille contigus.
export const BADGES = [
    // Plongées (nombre)
    { key: "baptism", cat: "dives", glyph: "bubbles", target: 1, metric: "dives", points: 5 },
    { key: "regular", cat: "dives", glyph: "bubbles", target: 10, metric: "dives", points: 10 },
    { key: "confirmed", cat: "dives", glyph: "bubbles", target: 25, metric: "dives", points: 20 },
    { key: "veteran", cat: "dives", glyph: "bubbles", target: 50, metric: "dives", points: 35 },
    { key: "centurion", cat: "dives", glyph: "bubbles", target: 100, metric: "dives", points: 60 },
    { key: "legend", cat: "dives", glyph: "star", target: 200, metric: "dives", points: 90 },
    { key: "myth", cat: "dives", glyph: "star", target: 500, metric: "dives", points: 150 },
    // Exploration — sites différents
    { key: "curious", cat: "explore", glyph: "pin", target: 5, metric: "sites", points: 10 },
    { key: "explorer", cat: "explore", glyph: "pin", target: 15, metric: "sites", points: 25 },
    { key: "siteCompletist", cat: "explore", glyph: "pin", target: 30, metric: "sites", points: 45 },
    { key: "cartographer", cat: "explore", glyph: "pin", target: 50, metric: "sites", points: 60 },
    // Exploration — même site
    { key: "regularSpot", cat: "explore", glyph: "pin", target: 5, metric: "topSite", points: 20 },
    { key: "spotPillar", cat: "explore", glyph: "pin", target: 10, metric: "topSite", points: 35 },
    // Exploration — cantons
    { key: "wanderer", cat: "explore", glyph: "compass", target: 3, metric: "cantons", points: 15 },
    { key: "tourDeSuisse", cat: "explore", glyph: "compass", target: 6, metric: "cantons", points: 35 },
    { key: "grandSlam", cat: "explore", glyph: "compass", target: 1, metric: "grandSlam", points: 60 },
    // Communauté — amis
    { key: "firstBuddy", cat: "social", glyph: "users", target: 1, metric: "friends", points: 5 },
    { key: "sociable", cat: "social", glyph: "users", target: 10, metric: "friends", points: 25 },
    // Communauté — binômes
    { key: "wellSurrounded", cat: "social", glyph: "users", target: 5, metric: "buddies", points: 25 },
    { key: "buddyMagnet", cat: "social", glyph: "users", target: 15, metric: "buddies", points: 30 },
    { key: "loyalBuddy", cat: "social", glyph: "users", target: 5, metric: "topBuddy", points: 30 },
    { key: "mentor", cat: "social", glyph: "users", target: 1, metric: "mentor", points: 30 },
    // Communauté — clubs & organisation
    { key: "member", cat: "social", glyph: "flag", target: 1, metric: "clubs", points: 10 },
    { key: "organizer", cat: "social", glyph: "calendar", target: 5, metric: "organized", points: 25 },
    { key: "gatherer", cat: "social", glyph: "calendar", target: 20, metric: "organized", points: 50 },
    { key: "publicHost", cat: "social", glyph: "flag", target: 10, metric: "publicOrganized", points: 40 }, // 10 plongées PUBLIQUES organisées (incite au public)
    // Communauté — profil & soutien
    { key: "completeProfile", cat: "social", glyph: "check", target: 1, metric: "profileComplete", points: 15 },
    { key: "supporter", cat: "social", glyph: "heart", target: 1, metric: "supporter", points: 10 },
    // Contribution (conditions)
    { key: "observer", cat: "contrib", glyph: "drop", target: 1, metric: "conditions", points: 10 },
    { key: "sentinel", cat: "contrib", glyph: "drop", target: 10, metric: "conditions", points: 25 },
    { key: "lookout", cat: "contrib", glyph: "drop", target: 25, metric: "conditions", points: 45 },
    // Variété — technique
    { key: "versatile", cat: "variety", glyph: "layers", target: 1, metric: "versatile", points: 20 }, // OC + CCR
    { key: "gasman", cat: "variety", glyph: "flame", target: 1, metric: "gasman", points: 30 },        // air + nitrox + trimix
    // Variété — rythme & régularité
    { key: "consistent", cat: "variety", glyph: "calendar", target: 3, metric: "streakMonths", points: 30 }, // 3 mois d'affilée
    { key: "marathon", cat: "variety", glyph: "flame", target: 5, metric: "bestMonth", points: 30 },   // 5 plongées / mois
    { key: "weekendWarrior", cat: "variety", glyph: "flame", target: 4, metric: "weekendMax", points: 30 }, // 4 plongées le même week-end
    { key: "ephemeris", cat: "variety", glyph: "calendar", target: 31, metric: "dayNumbers", points: 45 },  // un jour de chaque (1-31)
    // Variété — moment / saison
    { key: "nightOwl", cat: "variety", glyph: "moon", target: 1, metric: "night", points: 20 },        // plongée de nuit
    { key: "earlyBird", cat: "variety", glyph: "sun", target: 1, metric: "earlyBird", points: 20 },    // plongée avant 8h
    { key: "allSeasons", cat: "variety", glyph: "sun", target: 1, metric: "allSeasons", points: 30 },  // 4 saisons
    { key: "frosty", cat: "variety", glyph: "snow", target: 1, metric: "frosty", points: 10 },         // plongée en hiver
    { key: "heatwave", cat: "variety", glyph: "flame", target: 1, metric: "summer", points: 20 },      // plongée en été
    // Variété — dates spéciales
    { key: "christmas", cat: "variety", glyph: "gift", target: 1, metric: "christmas", points: 25 },   // 25 décembre
    { key: "valentine", cat: "variety", glyph: "heart", target: 1, metric: "valentine", points: 25 },  // 14 février
    { key: "newYear", cat: "variety", glyph: "sun", target: 1, metric: "newYear", points: 25 },        // 1er janvier
    { key: "birthday", cat: "variety", glyph: "gift", target: 1, metric: "birthday", points: 25 },     // jour de son anniversaire
];

// Total de points distribuables (somme de tous les badges).
export const TOTAL_POINTS = BADGES.reduce((s, b) => s + (b.points || 0), 0);

// Rangs de plongeur·euse selon les points cumulés (style "hauts faits").
// min = seuil de points pour atteindre le rang. Libellés via i18n ranks.<key>.
// Seuils calibrés sur ~1155 pts au total. Le dernier rang (900) est quasi impossible
// sans le badge Mythe (500 plongées, 300 pts) → réservé aux ~1-5% les plus assidus.
export const RANKS = [
    { key: "beginner", min: 0 },
    { key: "leisure", min: 100 },
    { key: "explorer", min: 200 },
    { key: "confirmed", min: 300 },
    { key: "veteran", min: 500 },
    { key: "master", min: 850 },
    { key: "legend", min: 1200 },
    { key: "poseidon", min: TOTAL_POINTS }, // = tous les badges (quasi mythique)
];

// Rang courant + suivant + progression (0-100) selon les points.
export function rankForPoints(points) {
    let idx = 0;
    for (let i = 0; i < RANKS.length; i++) if (points >= RANKS[i].min) idx = i;
    const current = RANKS[idx];
    const next = RANKS[idx + 1] || null;
    const nextMin = next ? next.min : TOTAL_POINTS;
    // Progression = points / seuil du prochain rang (intuitif : "120 sur 175").
    const pct = next ? Math.max(0, Math.min(100, Math.round((points / nextMin) * 100))) : 100;
    return { current, next, nextMin, pct, index: idx, total: RANKS.length };
}
